import React, { useEffect, useState } from 'react';
import type { User, PathwayBlock, PracticalEvaluation } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, CheckCircle, XCircle, Clock, Shield } from 'lucide-react';

interface PracticalBlockViewProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
}

export const PracticalBlockView: React.FC<PracticalBlockViewProps> = ({ user, block, pathwayId }) => {
  const { t } = useTranslation();
  const [evaluation, setEvaluation] = useState<PracticalEvaluation | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const config = block.practicalConfig;

  useEffect(() => {
    const load = async () => {
      try {
        const ev = await DataService.getPracticalEvaluation(user.id, pathwayId, block.id);
        setEvaluation(ev);

        if (ev?.overallResult === 'pass') {
          await DataService.setBlockProgress({
            candidateId: user.id,
            pathwayId,
            blockId: block.id,
            status: 'complete',
            completedAt: ev.evaluatedAt,
          });
        }
      } catch (e) {
        console.error('Failed to load practical evaluation', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, block.id, pathwayId]);

  const statusBg = !evaluation
    ? 'bg-aero-50 border-aero-200'
    : evaluation.overallResult === 'pass'
    ? 'bg-green-50 border-green-200'
    : 'bg-red-50 border-red-200';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      {/* Block header */}
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-aero-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <ClipboardCheck className="w-5 h-5 text-aero-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">{t('candidate.block.practical.title')}</p>
            <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
            {block.description && <p className="text-sm text-aero-600 mt-1">{block.description}</p>}
          </div>
        </div>
      </div>

      {/* Status card */}
      <div className={`rounded-xl border-2 p-5 ${statusBg}`}>
        {!evaluation ? (
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-aero-400" />
            <div>
              <p className="text-sm font-semibold text-aero-700">{t('candidate.block.practical.pendingAssessment')}</p>
              <p className="text-xs text-aero-500 mt-0.5">{t('candidate.block.practical.pendingAssessmentDesc')}</p>
            </div>
          </div>
        ) : evaluation.overallResult === 'pass' ? (
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-sm font-semibold text-green-700">{t('common.complete')}</p>
              <p className="text-xs text-green-600 mt-0.5">
                {t('candidate.block.practical.evaluatedBy', { name: evaluation.evaluatorName })} · {new Date(evaluation.evaluatedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm font-semibold text-red-700">{t('candidate.block.practical.failed')}</p>
              <p className="text-xs text-red-600 mt-0.5">
                {t('candidate.block.practical.evaluatedBy', { name: evaluation.evaluatorName })} · {new Date(evaluation.evaluatedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Rubric items */}
      {config?.rubric && config.rubric.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-aero-700 mb-2">{t('candidate.block.practical.competencies')}</h3>
          <div className="space-y-2">
            {config.rubric.map((item) => {
              const result = evaluation?.rubricResults?.find(r => r.rubricItemId === item.id);
              return (
                <div key={item.id} className="bg-white rounded-xl border border-aero-200 p-4">
                  <div className="flex items-start gap-3">
                    {result ? (
                      result.passed
                        ? <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-aero-300 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-semibold text-aero-900">{item.title}</p>
                      {item.description && <p className="text-xs text-aero-500 mt-0.5">{item.description}</p>}
                      {result?.comment && (
                        <p className="text-xs text-aero-600 mt-1 italic">"{result.comment}"</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Authorized roles */}
      {config?.authorizedRoles && config.authorizedRoles.length > 0 && (
        <div className="bg-aero-50 rounded-xl border border-aero-200 p-4 flex items-start gap-3">
          <Shield className="w-4 h-4 text-aero-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-aero-600">{t('candidate.block.practical.authorizedRoles')}</p>
            <p className="text-xs text-aero-500 mt-0.5">
              {config.authorizedRoles.map(r => r.replace('_', ' ')).join(', ')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
